import type { MemberUser } from "@/lib/types";

function formatLastVisitJa(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return "—";
  }
}

function CircleStat({ value, line2, label }: { value: string | number; line2: string; label: string }) {
  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <div
        className="w-[4.5rem] h-[4.5rem] sm:w-20 sm:h-20 rounded-full flex flex-col items-center justify-center shrink-0"
        style={{
          border: "2px solid rgba(212,175,55,0.65)",
          background: "rgba(212,175,55,0.06)",
          boxShadow: "0 0 20px rgba(212,175,55,0.12)",
        }}
      >
        <span
          className="text-xl sm:text-2xl font-bold leading-none tabular-nums"
          style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
        >
          {value}
        </span>
        <span className="text-[9px] font-bold mt-0.5" style={{ color: "rgba(244,229,195,0.55)" }}>
          {line2}
        </span>
      </div>
      <p
        className="text-[10px] mt-2 text-center leading-snug px-1"
        style={{ color: "rgba(244,229,195,0.5)" }}
      >
        {label}
      </p>
    </div>
  );
}

type Props = {
  user: MemberUser;
};

export default function VisitSummaryCard({ user }: Props) {
  const visitCount = user.visitCount ?? 0;
  const baseDays = user.recommendedVisitAfterDays ?? 25;
  const since = user.daysSinceLastVisit;
  const until = user.daysUntilRecommendedVisit;
  const hasLast = user.lastVisitAt != null && since != null;

  const sinceValue = hasLast ? String(since) : "—";
  const sinceLine2 = hasLast ? "日" : "—";
  const sinceLabel = hasLast ? "前回来店からの経過" : "前回来店（まだ記録なし）";

  let untilValue: string | number = "—";
  let untilLine2 = "—";
  let untilLabel = "おすすめ来店まで あと（目安: 約" + baseDays + "日）";
  if (until != null && until !== undefined) {
    if (until > 0) {
      untilValue = until;
      untilLine2 = "日";
      untilLabel = "おすすめ来店まで あと（目安: 約" + baseDays + "日）";
    } else if (until === 0) {
      untilValue = "0";
      untilLine2 = "日";
      untilLabel = "本日頃がおすすめの来店目安です";
    } else {
      untilValue = Math.abs(until);
      untilLine2 = "日";
      untilLabel = "おすすめ目安からの超過";
    }
  } else if (!hasLast) {
    untilLabel = "おすすめ来店まで あと（来店後に表示）";
  }

  return (
    <div
      className="rounded-2xl px-4 py-4 space-y-3"
      style={{
        background: "linear-gradient(135deg, rgba(26,47,47,0.95) 0%, rgba(13,31,31,0.98) 100%)",
        border: "1px solid rgba(212,175,55,0.22)",
      }}
    >
      <div className="flex items-center gap-2">
        <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
        <span className="text-xs tracking-widest" style={{ color: "rgba(212,175,55,0.5)", fontFamily: "Montserrat, sans-serif" }}>
          VISIT
        </span>
        <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
      </div>

      <div className="flex items-start justify-center gap-4 sm:gap-8">
        <CircleStat value={sinceValue} line2={sinceLine2} label={sinceLabel} />
        <CircleStat value={untilValue} line2={untilLine2} label={untilLabel} />
      </div>

      <div
        className="flex flex-wrap items-center justify-between gap-2 text-xs rounded-xl px-3 py-2.5"
        style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.15)" }}
      >
        <div>
          <span style={{ color: "rgba(244,229,195,0.45)" }}>累計来店</span>{" "}
          <span className="font-bold tabular-nums" style={{ color: "#d4af37", fontFamily: "Montserrat, sans-serif" }}>
            {visitCount}
          </span>
          <span style={{ color: "rgba(244,229,195,0.55)" }}> 回</span>
        </div>
        <div className="text-right min-w-0">
          <span style={{ color: "rgba(244,229,195,0.45)" }}>最終来店日</span>
          <br />
          <span className="font-medium" style={{ color: "#f4e5c3" }}>
            {formatLastVisitJa(user.lastVisitAt)}
          </span>
        </div>
      </div>

      <p className="text-[10px] leading-relaxed text-center" style={{ color: "rgba(244,229,195,0.38)" }}>
        おすすめの間隔は前回のご来店から約 {baseDays} 日（3〜4週間の目安）です。体調に合わせて無理のないペースでどうぞ。
      </p>
    </div>
  );
}
